import type { HubSocketMessage } from "@/lib/realtime/hub-events";

export type HubEventBatch = {
  /** Customers list, folder counts and the matching detail queries are stale. */
  customers: boolean;
  /** Every cached customer detail is stale: a resync, or a bulk change with no end-user id. */
  allCustomerDetails: boolean;
  endUserIds: Set<string>;
  /** At least one input announced a transport gap rather than one changed customer. */
  resync: boolean;
};

const BASE_DELAY_MS = 1_500;
const MAX_JITTER_MS = 1_000;

/**
 * Collapse a burst of hub events into one flush. The jitter keeps every open tab in a tenant
 * from refetching in lockstep after the same event.
 */
export class HubEventCoalescer {
  private batch: HubEventBatch | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly flush: (batch: HubEventBatch) => void,
    private readonly random: () => number = Math.random
  ) {}

  push(message: HubSocketMessage): void {
    const batch = (this.batch ??= {
      customers: false,
      allCustomerDetails: false,
      endUserIds: new Set(),
      resync: false,
    });
    batch.customers = true;
    if (message.type === "resync" || !message.endUserId) {
      batch.allCustomerDetails = true;
    } else {
      batch.endUserIds.add(message.endUserId);
    }
    batch.resync ||= message.type === "resync";

    if (!this.timer) {
      this.timer = setTimeout(() => this.fire(), BASE_DELAY_MS + this.random() * MAX_JITTER_MS);
    }
  }

  dispose(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = null;
    this.batch = null;
  }

  private fire(): void {
    const batch = this.batch;
    this.timer = null;
    this.batch = null;
    if (batch) {
      this.flush(batch);
    }
  }
}
